import type { InFieldCDMLocationConfig } from '../../../features/app-config/cdm-types';
import { mapInfieldLocationConfigToRootLocationConfig } from '../../../features/app-config/hooks/utils/cdm-map-infield-location-config-to-root-location-config';
import type {
  AppConfig,
  RootLocationConfiguration,
} from '../../../features/app-config/types';

/**
 * Converts InFieldCDMLocationConfig (IDM) to an AppConfig for backward compatibility.
 *
 * Uses the selected location config for top-level AppConfig fields (name, disciplines, etc.)
 * and converts all provided location configs into the rootLocationConfigurations field.
 *
 * @param selectedInfieldLocation - The selected location config used for top-level AppConfig fields
 * @param allInfieldLocations - Array of all location configs to convert into rootLocationConfigurations
 * @returns The converted AppConfig
 */
export const convertInfieldLocationConfigToAppConfig = (
  selectedInfieldLocation: InFieldCDMLocationConfig,
  allInfieldLocations: InFieldCDMLocationConfig[]
): AppConfig => {
  if (allInfieldLocations.length === 0) {
    throw new Error(
      'allInfieldLocations cannot be empty. At least one location config is required.'
    );
  }

  const isSelectedLocationInList = allInfieldLocations.some(
    (location) =>
      location.externalId === selectedInfieldLocation.externalId &&
      location.space === selectedInfieldLocation.space
  );

  if (!isSelectedLocationInList) {
    throw new Error(
      `Selected location (${selectedInfieldLocation.externalId}, ${selectedInfieldLocation.space}) is not present in allInfieldLocations array.`
    );
  }

  const primaryConfig = selectedInfieldLocation;

  return {
    name: primaryConfig?.name ?? 'InField IDM',
    externalId: 'not-used-idm-config',
    appDataSpaceId: 'not-used-app-data-space-id',
    appDataSpaceVersion: 'not-used-app-data-space-version',
    customerDataSpaceId: 'not-used-customer-data-space-id',
    customerDataSpaceVersion: 'not-used-customer-data-space-version',
    featureConfiguration: {
      rootLocationConfigurations: allInfieldLocations
        .map(mapInfieldLocationConfigToRootLocationConfig)
        .filter(
          (config): config is RootLocationConfiguration => config !== undefined
        ),
      disciplines: primaryConfig?.disciplines,
      observations: primaryConfig?.featureToggles?.observations
        ? {
            enabled: primaryConfig.featureToggles.observations,
            sapWriteBack: { enabled: false },
            optionalMediaField: { enabled: false },
          }
        : undefined,
      copilot: {
        enabled: primaryConfig?.featureToggles?.copilot ?? false,
      },
    },
    fieldConfiguration: {},
    rootLocationsConfiguration: {
      locations: [],
    },
    createdOn: new Date(primaryConfig?.createdTime ?? 0),
    isActive: true,
    isDefault: false,
  };
};
